import {useQuery, UseQueryResult} from 'react-query';
import {FeaturePointerVO, LayerVO, ServicesApi} from '../../api';
import {useMapioContext} from '../../mapioContext';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {useAppConfig} from '@mc/react-app-base';
import {CACHE_KEY_SEARCH_CARDS} from '../../queryKeys';
import {mapFeatureCardToViewportResult} from '../../helpers/featureCard';
import {ViewportResult} from './searchPanelTypes';

/**
 * Dotáhne bohaté karty (foto + adresa + přístupnost) pro nalezené OBJEKTY (POI) ve fulltextovém hledání,
 * aby se v panelu vykreslily stejně jako ve výřezovém seznamu. Jedním batchem přes `ServicesApi.getFeatureCards`
 * (stejný endpoint i mapper jako {@link useViewportFeatures}). Vrací mapu `layerId:featureId` → {@link ViewportResult}.
 * Ulice/adresy (LOCATION bez featureId) sem nepatří – ty se vykreslují jako prosté řádky.
 */
export const useSearchResultCards = (poiPointers: FeaturePointerVO[]): {cardsById: {[id: string]: ViewportResult}; isLoading: boolean} => {
    const mapioContext = useMapioContext();
    const {topicId, context} = mapioContext.value;
    const layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}> = useLayersConfig();
    // @ts-ignore
    const {serverContextPath} = useAppConfig();

    // Stabilní klíč z pointerů (pole chodí nové při každém hledání).
    const idsKey = poiPointers.map(p => `${p.layerId}:${p.featureId}`).join(',');

    const query = useQuery<{[id: string]: ViewportResult}>(
        [CACHE_KEY_SEARCH_CARDS, topicId, context ?? '', idsKey],
        async () => {
            const cards = (await ServicesApi.getFeatureCards(topicId, context ?? undefined, poiPointers)).data ?? [];
            const layersConfig = layersConfigQuery.data ?? {};
            const map: {[id: string]: ViewportResult} = {};
            cards.forEach(card => {
                const row = mapFeatureCardToViewportResult(card, layersConfig, serverContextPath);
                map[row.id] = row;
            });
            return map;
        },
        {
            enabled: !!topicId && !!layersConfigQuery.data && poiPointers.length > 0,
            staleTime: Infinity,
            keepPreviousData: true,
        },
    );

    return {cardsById: query.data ?? {}, isLoading: query.isFetching && poiPointers.length > 0 && !query.data};
};
